import { useState, useEffect, useRef } from "react";
import api from "@/api";
import { toast } from "sonner";
import {
  RefreshCw, Upload, Trash2, Play, Music, Volume2, Check
} from "lucide-react";

const AUDIO_KEYS = [
  { key: "step1_intro", label: "Introduction Étape 1 - Effets indésirables", category: "Étape 1 - Effets indésirables" },
  { key: "fatigue", label: "Fatigue", category: "Étape 1 - Effets indésirables" },
  { key: "pain", label: "Douleur", category: "Étape 1 - Effets indésirables" },
  { key: "nausea", label: "Nausées / Vomissements", category: "Étape 1 - Effets indésirables" },
  { key: "diarrhea", label: "Diarrhée", category: "Étape 1 - Effets indésirables" },
  { key: "constipation", label: "Constipation", category: "Étape 1 - Effets indésirables" },
  { key: "dyspnea", label: "Difficultés respiratoires", category: "Étape 1 - Effets indésirables" },
  { key: "appetite", label: "Perte d'appétit", category: "Étape 1 - Effets indésirables" },
  { key: "neuropathy", label: "Fourmillements / Engourdissements", category: "Étape 1 - Effets indésirables" },
  { key: "mucositis", label: "Douleur bouche / Aphtes", category: "Étape 1 - Effets indésirables" },
  { key: "rash", label: "Rash cutané / Problèmes de peau", category: "Étape 1 - Effets indésirables" },
  { key: "bleeding", label: "Saignements inhabituels", category: "Étape 1 - Effets indésirables" },
  { key: "edema", label: "Gonflements / Oedèmes", category: "Étape 1 - Effets indésirables" },
  { key: "perceivedFever", label: "Fièvre ressentie / Frissons", category: "Étape 1 - Effets indésirables" },
  { key: "step2_intro", label: "Introduction Étape 2 - Constantes", category: "Étape 2 - Constantes vitales" },
  { key: "temperature", label: "Température", category: "Étape 2 - Constantes vitales" },
  { key: "heartRate", label: "Pouls", category: "Étape 2 - Constantes vitales" },
  { key: "bloodPressure", label: "Tension artérielle", category: "Étape 2 - Constantes vitales" },
  { key: "spo2", label: "Oxygène (SpO2)", category: "Étape 2 - Constantes vitales" },
  { key: "weight", label: "Poids", category: "Étape 2 - Constantes vitales" },
  { key: "home_symptoms", label: "Accueil - Dire mes symptômes", category: "Accueil patient" },
  { key: "home_report_problem", label: "Accueil - Signaler un problème", category: "Accueil patient" },
  { key: "home_questions", label: "Accueil - Répondre aux questions", category: "Accueil patient" },
  { key: "home_medicine", label: "Accueil - Confirmer mon médicament", category: "Accueil patient" },
  { key: "home_bilan", label: "Accueil - Envoyer mon bilan", category: "Accueil patient" },
  { key: "home_team", label: "Accueil - Écrire à l’équipe", category: "Accueil patient" },
  { key: "home_documents", label: "Accueil - Voir mes documents", category: "Accueil patient" },
  { key: "home_appointments", label: "Accueil - Voir mes rendez-vous", category: "Accueil patient" },
  { key: "home_advice", label: "Accueil - Lire mes conseils", category: "Accueil patient" },
  { key: "home_emergency", label: "Accueil - Appeler le 141", category: "Accueil patient" },
];

export default function AudioLibraryTab() {
  const [audios, setAudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [language, setLanguage] = useState("fr");
  const audioRef = useRef(null);

  const fetchAudios = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/audios?language=${language}`);
      setAudios(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement des audios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAudios(); }, [language]);

  const handleUpload = async (audioKey, file) => {
    if (!file) return;
    setUploading(audioKey);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("audioKey", audioKey);
    formData.append("language", language);

    try {
      await api.post("/audios", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Audio téléchargé avec succès");
      fetchAudios();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du téléchargement");
    } finally {
      setUploading(null);
    }
  };

  const handleDelete = async (audioId) => {
    if (!confirm("Supprimer cet audio ?")) return;
    try {
      await api.delete(`/audios/${audioId}`);
      toast.success("Audio supprimé");
      fetchAudios();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handlePlay = async (audioId) => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (playingId === audioId) { setPlayingId(null); return; }

    const audioMeta = audios.find(a => a.id === audioId);
    const cacheBust = audioMeta?.updatedAt || audioMeta?.createdAt || Date.now();

    try {
      // Fetch through the authenticated API client, then play from a blob URL.
      // Required because <audio src="/api/..."> cannot attach Authorization headers in production.
      const res = await api.get(`/audios/${audioId}/play?v=${encodeURIComponent(cacheBust)}`, {
        responseType: "blob",
      });
      const blob = res.data;
      const blobUrl = URL.createObjectURL(blob);

      const audio = new Audio(blobUrl);
      audioRef.current = audio;
      const cleanup = () => { try { URL.revokeObjectURL(blobUrl); } catch { return; } };
      audio.onended = () => { setPlayingId(null); cleanup(); };
      audio.onerror = () => {
        const err = audio.error;
        console.error("[AudioLibrary] decode error", { code: err?.code, msg: err?.message, type: blob.type, size: blob.size });
        setPlayingId(null);
        cleanup();
        toast.error(`Fichier non décodable (code ${err?.code ?? "?"}, type=${blob.type || "?"}).`);
      };
      await audio.play();
      setPlayingId(audioId);
    } catch (err) {
      console.error("[AudioLibrary] play() rejected", err);
      toast.error(`Erreur de lecture : ${err?.message || err}`);
    }
  };

  const categories = [...new Set(AUDIO_KEYS.map(a => a.category))];
  const audioMap = {};
  audios.forEach(a => { audioMap[a.audioKey] = a; });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Music className="w-5 h-5 text-emerald-600" />
            Bibliothèque Audio - Check-in Patient
          </h2>
          <p className="text-sm text-slate-500 mt-1">Téléchargez les fichiers audio pour guider les patients pendant leur check-in</p>
        </div>
        <select value={language} onChange={e => setLanguage(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="audio-library-language-select">
          <option value="fr">Français</option>
          <option value="ar">Arabe</option>
        </select>
      </div>

      <div className="dash-card">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-700">Progression</span>
          <span className="text-sm text-slate-500" data-testid="audio-library-progress-count">{audios.length} / {AUDIO_KEYS.length} audios</span>
        </div>
        <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all" style={{ width: `${(audios.length / AUDIO_KEYS.length) * 100}%` }} />
        </div>
      </div>

      {categories.map(category => (
        <div key={category} className="dash-card" data-testid={`audio-library-category-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Volume2 className="w-4 h-4 text-emerald-600" />
            {category}
          </h3>
          <div className="space-y-3">
            {AUDIO_KEYS.filter(a => a.category === category).map(audioItem => {
              const existingAudio = audioMap[audioItem.key];
              const isUploading = uploading === audioItem.key;
              const isPlaying = playingId === existingAudio?.id;
              return (
                <div key={audioItem.key} className={`flex items-center justify-between p-3 rounded-xl border ${existingAudio ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`} data-testid={`audio-library-row-${audioItem.key}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${existingAudio ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-200 text-slate-400'}`}>
                      {existingAudio ? <Check className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{audioItem.label}</p>
                      <p className="text-xs text-slate-500">Clé: {audioItem.key}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {existingAudio && (
                      <>
                        <button onClick={() => handlePlay(existingAudio.id)} className={`p-2 rounded-lg transition-colors ${isPlaying ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'}`} title="Écouter" data-testid={`audio-library-play-${audioItem.key}`}><Play className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(existingAudio.id)} className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors" title="Supprimer" data-testid={`audio-library-delete-${audioItem.key}`}><Trash2 className="w-4 h-4" /></button>
                      </>
                    )}
                    <label className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${existingAudio ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`} data-testid={`audio-library-upload-label-${audioItem.key}`}>
                      {isUploading ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <Upload className="w-4 h-4" />}
                      <span className="text-sm font-medium">{existingAudio ? 'Remplacer' : 'Télécharger'}</span>
                      <input type="file" accept="audio/*,audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/m4a,audio/x-m4a,audio/aac,audio/ogg,.mp3,.wav,.m4a,.aac,.ogg" className="hidden" onChange={e => handleUpload(audioItem.key, e.target.files?.[0])} disabled={isUploading} data-testid={`audio-library-upload-input-${audioItem.key}`} />
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {loading && (
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 mx-auto text-slate-400 animate-spin" />
          <p className="text-slate-500 mt-2">Chargement...</p>
        </div>
      )}
    </div>
  );
}
